import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Button, Form, Row, Col, Container } from "react-bootstrap";
import FormInput from "../../components/ui/FormInput";
import { loanDetailsFields, handleFileChange } from "../../utils/utils";
import axios from "axios";
import { API_BASE_URL } from "../../config";
import { useNavigate } from "react-router-dom";
import ErrorMessage from "../../components/shared/ErrorMessage";

const LoanDetails = ({
  memberId,
  loanDetailsData,
  setLoanDetailsData,
  handleNext,
  type,
  locked = false,
}) => {
  const navigate = useNavigate();
  const [isEditingRaw, setIsEditingRaw] = useState(type === "Edit");
  // `locked` wins over this component's own isEditing. Passing type="View" is
  // NOT enough: the Edit button below renders *because* the type is View, and
  // clicking it sets isEditing = true, which would reopen a disbursed
  // application. Defaults to false so the Customers flow is unaffected.
  const isEditing = locked ? false : isEditingRaw;
  const setIsEditing = (v) => { if (!locked) setIsEditingRaw(v); };
  const [errorMsg, setErrorMsg] = useState("");
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    reset,
  } = useForm({
    defaultValues: loanDetailsData,
  });

  const [filePreviews, setFilePreviews] = useState({});
  const [existingFiles, setExistingFiles] = useState({});
  const [fieldsData, setFieldsData] = useState(loanDetailsFields);

  useEffect(() => {
    if (loanDetailsData) {
      reset(loanDetailsData); // Reset form with new data
      Object.keys(loanDetailsData).forEach((key) => {
        setValue(key, loanDetailsData[key]);
        if (key.endsWith("Image") && loanDetailsData[key]) {
          setExistingFiles((prev) => ({
            ...prev,
            [key]: `${API_BASE_URL}/images/${loanDetailsData[key]}`,
          }));
        }
      });
    }
  }, [loanDetailsData, setValue, reset]);

  const onSubmit = async (submittedData) => {
    // Insert or update is decided by WHETHER THIS SECTION EXISTS, not by the
    // URL's `type`. That flag describes the loan; this tab is optional, so a
    // loan edited here may never have had a row at all.
    //
    // This one never put an id in the payload at all — it relied on
    // `...submittedData` carrying one, which happens only when the form was
    // populated from a saved row. So an edit failed with "Update requires a
    // valid ID." whether or not the row existed. 5,771 of 6,414 loans have none.
    const existingId = loanDetailsData?.id;
    const isNew = !existingId;

    const { id: _ignoredId, ...fields } = submittedData;

    const payload = {
      ...fields,
      model: "loan_details",
      type: isNew ? "Add" : "Edit",
      // Required, or the new row is orphaned with no loan.
      memberId,
    };
    if (!isNew) payload.id = existingId;

    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/addDetails`,
        payload
      );

      if (response.status === 201) {
        setLoanDetailsData(response.data.savedData);
        console.log("Loan Details updated successfully: ", response.data);
        setIsEditing(false);

        if (type === "Add") {
          handleNext();
        }
        if (isEditing) {
          navigate("/addEditBusinessLoan", {
            state: { type: "View", stepNo: 6, memberId },
          });
        }
        setErrorMsg("");
      } else {
        setErrorMsg(response.data.error || "Error updating loan details");
      }
    } catch (error) {
      console.error("Error updating Loan Details: ", error);
      const msg = error.response?.data?.error || error.response?.data?.message || error.message || "Error updating loan details";
      setErrorMsg(msg);
    }
  };

  const onError = (errors) => {
    const errorMessages = Object.keys(errors).reduce((acc, key) => {
      acc[key] = errors[key].message;
      return acc;
    }, {});
    console.log("Form errors:", errorMessages);
  };

  const handleEditClick = () => {
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    reset(loanDetailsData); // Reset to original data
    setFilePreviews({}); // Clear any new file previews
  };

  return (
    <Container className="mt-4">
      <h2 className="text-center mb-4">
        Loan Details
        {!locked && type === "View" && !isEditing && (
          <Button
            variant="primary"
            className="ms-3 mt-2 btn-hover "
            onClick={handleEditClick}
          >
            Edit
          </Button>
        )}
      </h2>
      <ErrorMessage message={errorMsg} />
      <Form onSubmit={handleSubmit(onSubmit, onError)}>
        <Row>
          {fieldsData.map((field, index) => (
            <Col
              xs={12}
              md={6}
              key={index}
              className="mb-3"
            >
              <FormInput
                label={field.label}
                type={field.type}
                name={field.name}
                register={register}
                watch={watch}
                errors={errors}
                options={field.options || []}
                readOnly={(type === "View" && !isEditing) || field.readOnly}
                handleFileChange={(e) =>
                  handleFileChange(e, field.name, setFilePreviews)
                }
                existingFileUrl={existingFiles[field.name] || ""}
                filePreview={filePreviews[field.name]?.previewUrl || ""}
                required={field.required}
              />
            </Col>
          ))}
        </Row>

        <div className="text-center mt-3">
          {(type !== "View" || isEditing) && (
            <Button variant="success" type="submit" className="me-2">
              {isEditing ? "Update" : "Submit"}
            </Button>
          )}
          {isEditing && (
            <Button
              variant="warning"
              onClick={handleCancelEdit}
              className="me-2"
            >
              Cancel
            </Button>
          )}
          {!locked && type === "View" && !isEditing && (
            <Button
              variant="secondary"
              onClick={() =>
                navigate("/addEditBusinessLoan", {
                  state: { type: "View", stepNo: 6, memberId },
                })
              }
            >
              Back
            </Button>
          )}
        </div>
      </Form>
    </Container>
  );
};

export default LoanDetails;